import { WebViewerInstance } from '@pdftron/webviewer';
import store from 'store/store';
import { getSelectedTool } from 'store';
import { PDFTronTools } from 'types';

/**
 * CrosshairManager - Manages extended crosshair overlay for drawing tools
 * Used by CropZone and MarkupZone to provide visual guides when drawing
 */
export class CrosshairManager {
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private lastMousePosition: { x: number; y: number } | null = null;
  private instance: WebViewerInstance;
  private allowedTool: PDFTronTools;
  private shouldDrawCallback?: (event: MouseEvent) => boolean;

  // Bound function references for event listener cleanup
  private boundUpdateCanvasSize: () => void;
  private boundOnMouseMove: EventListener;
  private boundOnMouseLeave: () => void;

  constructor(
    instance: WebViewerInstance,
    allowedTool: PDFTronTools,
    shouldDrawCallback?: (event: MouseEvent) => boolean,
  ) {
    this.instance = instance;
    this.allowedTool = allowedTool;
    this.shouldDrawCallback = shouldDrawCallback;

    // Bind methods once to maintain consistent references
    this.boundUpdateCanvasSize = this.updateCanvasSize.bind(this);
    this.boundOnMouseMove = this.onMouseMove.bind(this) as EventListener;
    this.boundOnMouseLeave = this.onMouseLeave.bind(this);
  }

  /**
   * Initialize the crosshair canvas and event listeners
   */
  setup(): void {
    const viewerElement = this.instance.Core.documentViewer.getScrollViewElement();
    if (!viewerElement) return;

    // Create canvas overlay
    this.canvas = document.createElement('canvas');
    this.canvas.id = `crosshair-canvas-${this.allowedTool}`;
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '999';

    viewerElement.appendChild(this.canvas);
    this.context = this.canvas.getContext('2d') as CanvasRenderingContext2D;

    // Add event listeners
    window.addEventListener('resize', this.boundUpdateCanvasSize);
    this.updateCanvasSize();

    viewerElement.addEventListener('mousemove', this.boundOnMouseMove);
    viewerElement.addEventListener('mouseleave', this.boundOnMouseLeave);
  }

  /**
   * Draw crosshair lines at specified coordinates
   */
  private drawCrosshairs(x: number, y: number): void {
    if (!this.context || !this.canvas) return;

    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Vertical line
    this.context.beginPath();
    this.context.moveTo(x + 0.5, 0);
    this.context.lineTo(x + 0.5, this.canvas.height);
    this.context.strokeStyle = '#6DDAE2';
    this.context.lineWidth = 0.5;
    this.context.stroke();

    // Horizontal line
    this.context.beginPath();
    this.context.moveTo(0, y + 0.5);
    this.context.lineTo(this.canvas.width, y + 0.5);
    this.context.strokeStyle = '#6DDAE2';
    this.context.lineWidth = 0.5;
    this.context.stroke();
  }

  /**
   * Clear the crosshair canvas
   */
  clearCanvas(): void {
    if (this.context && this.canvas) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /**
   * Update canvas size to match viewer element
   */
  private updateCanvasSize(): void {
    if (!this.canvas) return;

    const viewerElement = this.instance.Core.documentViewer.getScrollViewElement();
    if (!viewerElement) return;

    this.canvas.width = viewerElement.clientWidth;
    this.canvas.height = viewerElement.clientHeight;

    // Redraw crosshairs at last known position
    if (this.lastMousePosition) {
      this.drawCrosshairs(this.lastMousePosition.x, this.lastMousePosition.y);
    }
  }

  /**
   * Handle mouse move events
   */
  private onMouseMove(event: MouseEvent): void {
    // Check if the correct tool is selected
    if (getSelectedTool(store.getState()) !== this.allowedTool) {
      this.clearCanvas();
      return;
    }

    // Check cursor style (should be crosshair for drawing)
    const cursorStyle = window.getComputedStyle(event.target as Element).cursor;
    if (cursorStyle !== 'crosshair') {
      this.clearCanvas();
      return;
    }

    // Execute custom callback if provided (e.g., check for existing crops/polygons)
    if (this.shouldDrawCallback && !this.shouldDrawCallback(event)) {
      this.clearCanvas();
      return;
    }

    if (!this.canvas) return;

    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.lastMousePosition = { x, y };
    this.drawCrosshairs(x, y);
  }

  /**
   * Handle mouse leave events
   */
  private onMouseLeave(): void {
    this.clearCanvas();
  }

  /**
   * Clean up and remove the crosshair canvas
   */
  cleanup(): void {
    this.clearCanvas();

    if (this.canvas && this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }

    const viewerElement = this.instance.Core.documentViewer.getScrollViewElement();
    if (viewerElement) {
      viewerElement.removeEventListener('mousemove', this.boundOnMouseMove);
      viewerElement.removeEventListener('mouseleave', this.boundOnMouseLeave);
    }

    window.removeEventListener('resize', this.boundUpdateCanvasSize);

    this.canvas = null;
    this.context = null;
    this.lastMousePosition = null;
  }
}
